import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import { AlertTriangle } from "lucide-react";
import PasswordInput from "@/components/ui/PasswordInput";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

const signupSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(8, { message: "Password must be at least 8 characters" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

interface SignupFormProps {
  className?: string;
  role?: 'guest' | 'operator';
}

function WarmAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-3 py-2 mt-2 shadow-sm text-sm">
      <AlertTriangle className="w-4 h-4 text-orange-400" />
      <span>{message}</span>
    </div>
  );
}

const SignupForm: React.FC<SignupFormProps> = ({ className, role = 'guest' }) => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: SignupFormValues) => {
    setLoading(true);
    try {
      await registerUser(data.name, data.email, data.password);
      navigate("/verify-email");
    } catch (error) {
      form.setError("root", {
        type: "manual",
        message: "Registration failed. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isGuestSignup = role === 'guest';

  return (
    <div className={cn("bg-sauna-beige min-h-screen flex items-start justify-center px-2 mt-4", className)}>
      <Card className="w-full max-w-md min-h-[500px] shadow-2xl border-none rounded-2xl p-0 sm:p-1 bg-white/90 flex flex-col justify-center">
        <CardHeader className="flex flex-col items-center pt-4 pb-2">
          <div className="rounded-full bg-gradient-to-b from-sauna-orange/80 to-sauna-orange p-3 mb-2 shadow-lg">
            <svg width="40" height="40" fill="none" viewBox="0 0 24 24"><path fill="#fff" d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6zm2 0v.01L12 13l8-6.99V6H4zm16 2.236l-7.445 6.5a1 1 0 0 1-1.11 0L4 8.236V18h16V8.236z"/></svg>
          </div>
          <CardTitle className="text-2xl font-bold text-sauna-navy mb-1 text-center">
            {isGuestSignup ? "Create your Account" : "Register your Business"}
          </CardTitle>
          <CardDescription className="text-base text-sauna-navy/80 mb-2 text-center font-medium">
            {isGuestSignup ? "Join SaunaDip today!" : "Start reaching new customers"}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-6 pt-0">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2" noValidate>
            <div>
              <Label htmlFor="name" className="text-sauna-navy font-semibold mb-1 block">Name</Label>
              <Input id="name" type="text" placeholder="Your name" {...form.register("name")}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg" />
              <span className="text-xs text-red-500 mt-1 block min-h-[1.25rem]">{form.formState.errors.name?.message}</span>
            </div>
            <div>
              <Label htmlFor="email" className="text-sauna-navy font-semibold mb-1 block">Email</Label>
              <Input id="email" type="email" placeholder="you@example.com" {...form.register("email")}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg" />
              <span className="text-xs text-red-500 mt-1 block min-h-[1.25rem]">{form.formState.errors.email?.message}</span>
            </div>
            <div>
              <Label htmlFor="password" className="text-sauna-navy font-semibold mb-1 block">Password</Label>
              <PasswordInput id="password" placeholder="Create a strong password" {...form.register("password")}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg" />
              <span className="text-xs text-red-500 mt-1 block min-h-[1.25rem]">{form.formState.errors.password?.message}</span>
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-sauna-navy font-semibold mb-1 block">Confirm Password</Label>
              <PasswordInput id="confirmPassword" placeholder="Confirm your password" {...form.register("confirmPassword")}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg" />
              <span className="text-xs text-red-500 mt-1 block min-h-[1.25rem]">{form.formState.errors.confirmPassword?.message}</span>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white text-lg py-2 mt-2 rounded-lg shadow-md hover:from-sauna-orange/90 hover:to-sauna-orange transition font-bold tracking-wide disabled:opacity-70 disabled:cursor-not-allowed">{loading ? "Creating Account..." : "Create Account"}</Button>
          </form>
          <div className="text-center mt-4 text-sauna-navy/80 text-base">
            Already have an account? <a href="/login" className="text-sauna-orange font-semibold hover:underline">Sign in</a>
          </div>
          {form.formState.errors.root && (
            <WarmAlert message={form.formState.errors.root.message as string} />
          )}
          <div className="text-xs text-center text-sauna-navy/60 mt-6 leading-relaxed" style={{ lineHeight: 1.7 }}>
            By creating an account, you agree to our <a href="/terms-and-conditions" className="text-sauna-orange underline">Terms of Service</a> and <a href="/privacy-policy" className="text-sauna-orange underline">Privacy Policy</a>.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupForm;
